/**
 * This is a helper function which finds the selection-node or the paragraph which
 * contains the selection-mark with the comment ID NEW_COMMENT_ID.
 *
 * @param editor - The editor instance to search within.
 * @returns A tuple containing:
 *   - The found node (selection-node or paragraph) or `null` (if not found).
 *   - The found mark or `null` (if not found).
 *   - The starting position of the found node or mark or `null` (if not found).
 *   - The ending position of the found mark or `null` (if not found or not a mark).
 */
import { Editor } from '@tiptap/core';
import { Mark, Node } from '@tiptap/pm/model';
import { NEW_COMMENT_ID, SELECTION_MARK_NAME, SELECTION_NODE_NAME } from '../constants';

const findNodeWithSelection = (editor: Editor): [Node | null, Mark | null, number, number] => {
  let foundNode: Node | null = null;
  let foundMark: Mark | null = null;
  let posStart = -1;
  let posEnd = -1;

  editor.state.doc.descendants((node: Node, pos: number, parent: Node | null) => {
    if (foundNode) return false;
    if (node.type.name === SELECTION_NODE_NAME && node.attrs.commentId === NEW_COMMENT_ID) {
      foundNode = node;
      posStart = pos;
      return false;
    }
    // Any textblock, not only paragraph - finds a comment mark inside a caption too.
    if (node.isTextblock && !foundNode) {
      let hasGap = false;
      node.descendants((child: Node, childPos: number) => {
        const mark = child.marks.find(
          (mark) => mark.type.name === SELECTION_MARK_NAME && mark.attrs.commentId === NEW_COMMENT_ID
        );

        if (mark && !hasGap) {
          if (!foundMark) {
            foundNode = node;
            foundMark = mark;
            posStart = pos + childPos + 1;
            posEnd = posStart;
          }

          posEnd += child.nodeSize;
        } else if (foundMark) hasGap = true;
      });
    }
  });

  return [foundNode, foundMark, posStart, posEnd];
};

export default findNodeWithSelection;
